import React, { useEffect, useState, useCallback } from 'react';
import { Container, Table, Badge, Button, Modal, Form } from 'react-bootstrap';
import { chatsApi } from '../../api';
import HrnbCard from '../../components/controls/HrnbCard';
import HrnbAlert from '../../components/controls/HrnbAlert';
import HrnbLoader from '../../components/controls/HrnbLoader';
import AdminPagination from '../components/pagination/AdminPagination';
import './AdminChats.css';

const AdminChats = () => {
    const [chats, setChats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [showMessagesModal, setShowMessagesModal] = useState(false);
    const [messagesLoading, setMessagesLoading] = useState(false);

    const [filterType, setFilterType] = useState('');

    const loadChats = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const params = { page: currentPage - 1, size: pageSize };
            if (filterType) params.type = filterType;
            const data = await chatsApi.listChats(params);
            const list = Array.isArray(data) ? data : (data?.content || []);
            const total = Array.isArray(data) ? data.length : (data?.totalElements || data?.total || list.length);
            setChats(list);
            setTotalItems(total);
        } catch (err) {
            setError('Не удалось загрузить список чатов');
            console.error('[AdminChats] Failed to load chats:', err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, filterType]);

    useEffect(() => {
        loadChats();
    }, [loadChats]);

    const handleViewMessages = async (chat) => {
        setSelectedChat(chat);
        setShowMessagesModal(true);
        setMessagesLoading(true);
        try {
            const data = await chatsApi.getMessages(chat.id, { limit: 50 });
            const list = Array.isArray(data) ? data : (data?.content || []);
            setMessages(list);
        } catch (err) {
            console.error('[AdminChats] Failed to load messages:', err);
            setMessages([]);
        } finally {
            setMessagesLoading(false);
        }
    };

    const handleDeleteChat = async (chat) => {
        if (!window.confirm(`Удалить чат "${chat.name || chat.id}"?`)) return;
        try {
            setActionLoading(true);
            await chatsApi.deleteChat(chat.id);
            await loadChats();
        } catch (err) {
            setError(`Не удалось удалить чат: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const getTypeBadge = (type) => {
        switch (type) {
            case 'DIRECT': return 'primary';
            case 'GROUP': return 'success';
            case 'BATTLE_SUPPORT': return 'warning';
            default: return 'secondary';
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('ru-RU');
    };

    if (loading && chats.length === 0) {
        return (
            <Container fluid className="admin-loading">
                <HrnbLoader />
            </Container>
        );
    }

    return (
        <Container fluid>
            <h1 className="admin-page-title">Управление чатами</h1>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            <HrnbCard>
                <div className="admin-pagination-header">
                    <Form.Select
                        className="admin-page-size-selector"
                        value={pageSize}
                        onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    >
                        <option value={5}>5 на странице</option>
                        <option value={10}>10 на странице</option>
                        <option value={25}>25 на странице</option>
                    </Form.Select>
                    <Form.Select
                        className="admin-filter-selector ms-2"
                        value={filterType}
                        onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }}
                        style={{ width: 'auto' }}
                    >
                        <option value="">Все типы</option>
                        <option value="DIRECT">Личные</option>
                        <option value="GROUP">Групповые</option>
                        <option value="BATTLE_SUPPORT">Поддержка баттлов</option>
                    </Form.Select>
                </div>

                <Table responsive hover className="admin-chats-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Название</th>
                            <th>Тип</th>
                            <th>Участников</th>
                            <th>Сообщений</th>
                            <th>Последнее сообщение</th>
                            <th>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {chats.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="text-center text-muted">
                                    Чаты не найдены
                                </td>
                            </tr>
                        ) : (
                            chats.map((chat) => (
                                <tr key={chat.id}>
                                    <td>{chat.id}</td>
                                    <td><strong>{chat.name || '—'}</strong></td>
                                    <td>
                                        <Badge bg={getTypeBadge(chat.type)}>
                                            {chat.type || '—'}
                                        </Badge>
                                    </td>
                                    <td>{chat.participantsCount || chat.participants?.length || 0}</td>
                                    <td>{chat.messagesCount || 0}</td>
                                    <td>{formatDate(chat.lastMessageAt || chat.updatedAt)}</td>
                                    <td>
                                        <Button
                                            variant="outline-info"
                                            size="sm"
                                            className="me-1"
                                            onClick={() => handleViewMessages(chat)}
                                            disabled={actionLoading}
                                        >
                                            Сообщения
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleDeleteChat(chat)}
                                            disabled={actionLoading}
                                        >
                                            Удалить
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>

                <AdminPagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                />
            </HrnbCard>

            {/* Messages Modal */}
            <Modal show={showMessagesModal} onHide={() => setShowMessagesModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Сообщения чата: {selectedChat?.name || selectedChat?.id}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {messagesLoading ? (
                        <HrnbLoader text="Загрузка сообщений..." />
                    ) : messages.length === 0 ? (
                        <p className="text-muted">Сообщений нет</p>
                    ) : (
                        <div className="admin-chats-messages">
                            {messages.map((msg) => (
                                <div key={msg.id} className="admin-chats-message">
                                    <div className="admin-chats-message-header">
                                        <strong>{msg.sender?.username || msg.sender || 'Аноним'}</strong>
                                        <span className="text-muted small">{formatDate(msg.createdAt || msg.timestamp)}</span>
                                    </div>
                                    <div className="admin-chats-message-body">{msg.content || msg.text}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowMessagesModal(false)}>
                        Закрыть
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminChats;
